import { SupabaseClient } from '@supabase/supabase-js'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { useSaveQuoteCanvasData } from 'src/api/db/apiQuote'
import { Database, type Json } from 'supabase/database.types'
import {
  isCanvasDataEmpty,
  parseCanvasData,
  toPersistedCanvasData,
} from './canvasDocument'
import {
  CANVAS_PERSIST_MS,
  FIT_PADDING,
  MAX_IMAGE_PREVIEW_BYTES,
  MAX_ZOOM,
  MIN_ZOOM,
  STROKE_COLOR,
  STROKE_WIDTH,
  ZOOM_STEP,
} from './consts'
import {
  clearLocalCanvasKeys,
  importLegacyLocalCanvas,
  readLocalCanvasDocument,
  writeLocalCanvasDocument,
} from './localStore'
import type {
  CanvasBounds,
  CanvasTransform,
  CardPosition,
  QuoteCanvasData,
} from './types'
import {
  clamp,
  createId,
  defaultPositionForIndex,
  fetchLinkPreview,
  readFileAsDataURL,
  uploadCanvasFile,
} from './utils'

export const useCanvasTransform = (
  containerRef: RefObject<HTMLDivElement | null>
) => {
  const [transform, setTransform] = useState<CanvasTransform>({
    x: 0,
    y: 0,
    scale: 1,
  })
  const transformRef = useRef(transform)
  transformRef.current = transform
  const isPinchingRef = useRef(false)

  const zoomAtPoint = useCallback((point: CardPosition, factor: number) => {
    setTransform((prev) => {
      const scale = clamp(prev.scale * factor, MIN_ZOOM, MAX_ZOOM)
      const ratio = scale / prev.scale
      return {
        scale,
        x: point.x - (point.x - prev.x) * ratio,
        y: point.y - (point.y - prev.y) * ratio,
      }
    })
  }, [])

  const getViewportCenter = useCallback((): CardPosition => {
    const el = containerRef.current
    return el
      ? { x: el.clientWidth / 2, y: el.clientHeight / 2 }
      : { x: 0, y: 0 }
  }, [containerRef])

  const zoomIn = useCallback(
    () => zoomAtPoint(getViewportCenter(), ZOOM_STEP),
    [zoomAtPoint, getViewportCenter]
  )

  const zoomOut = useCallback(
    () => zoomAtPoint(getViewportCenter(), 1 / ZOOM_STEP),
    [zoomAtPoint, getViewportCenter]
  )

  const resetZoom = useCallback(() => {
    const point = getViewportCenter()
    setTransform((prev) => {
      const ratio = 1 / prev.scale
      return {
        scale: 1,
        x: point.x - (point.x - prev.x) * ratio,
        y: point.y - (point.y - prev.y) * ratio,
      }
    })
  }, [getViewportCenter])

  const panBy = useCallback((dx: number, dy: number) => {
    setTransform((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }))
  }, [])

  const fitToBounds = useCallback(
    (bounds: CanvasBounds | null) => {
      const el = containerRef.current
      if (!el || !bounds) return
      const isNarrow = el.clientWidth < 640
      const padX = isNarrow ? 20 : FIT_PADDING
      const padY = isNarrow ? 80 : FIT_PADDING
      const width = Math.max(bounds.maxX - bounds.minX, 1)
      const height = Math.max(bounds.maxY - bounds.minY, 1)
      const scale = clamp(
        Math.min(
          (el.clientWidth - padX * 2) / width,
          (el.clientHeight - padY * 2) / height,
          1
        ),
        MIN_ZOOM,
        MAX_ZOOM
      )
      setTransform({
        scale,
        x: (el.clientWidth - width * scale) / 2 - bounds.minX * scale,
        y: (el.clientHeight - height * scale) / 2 - bounds.minY * scale,
      })
    },
    [containerRef]
  )

  // Native (non-passive) wheel listener so we can preventDefault browser
  // zoom/scroll. Trackpad pinch arrives as wheel + ctrlKey.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      if (e.ctrlKey || e.metaKey) {
        const rect = el.getBoundingClientRect()
        zoomAtPoint(
          { x: e.clientX - rect.left, y: e.clientY - rect.top },
          Math.exp(-e.deltaY * 0.01)
        )
      } else {
        setTransform((prev) => ({
          ...prev,
          x: prev.x - e.deltaX,
          y: prev.y - e.deltaY,
        }))
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [containerRef, zoomAtPoint])

  // Touch pinch-zoom / two-finger pan. Capture-phase pointer tracking so
  // cards don't steal the second finger; non-passive touch/gesture listeners
  // so iOS Safari doesn't zoom the page instead.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const pointers = new Map<number, CardPosition>()
    let pinch: {
      startDist: number
      startMid: CardPosition
      start: CanvasTransform
    } | null = null

    const localPoint = (e: PointerEvent): CardPosition => {
      const rect = el.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const beginPinch = () => {
      const pts = [...pointers.values()]
      if (pts.length < 2) return
      const [a, b] = pts
      pinch = {
        startDist: Math.max(Math.hypot(a.x - b.x, a.y - b.y), 1),
        startMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        start: { ...transformRef.current },
      }
      isPinchingRef.current = true
      for (const id of pointers.keys()) {
        try {
          el.setPointerCapture(id)
        } catch {
          // Pointer may already be released.
        }
      }
    }

    const endPinch = () => {
      pinch = null
      isPinchingRef.current = false
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return
      pointers.set(e.pointerId, localPoint(e))
      if (pointers.size >= 2) {
        beginPinch()
        e.stopPropagation()
      }
    }

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return
      pointers.set(e.pointerId, localPoint(e))
      if (!pinch || pointers.size < 2) return
      e.preventDefault()
      e.stopPropagation()
      const pts = [...pointers.values()]
      const [a, b] = pts
      const dist = Math.max(Math.hypot(a.x - b.x, a.y - b.y), 1)
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const scale = clamp(
        pinch.start.scale * (dist / pinch.startDist),
        MIN_ZOOM,
        MAX_ZOOM
      )
      const ratio = scale / pinch.start.scale
      setTransform({
        scale,
        x: mid.x - (pinch.startMid.x - pinch.start.x) * ratio,
        y: mid.y - (pinch.startMid.y - pinch.start.y) * ratio,
      })
    }

    const onPointerUp = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId) && !isPinchingRef.current) return
      pointers.delete(e.pointerId)
      if (pointers.size < 2) pinch = null
      if (isPinchingRef.current) {
        e.stopPropagation()
        if (pointers.size === 0) isPinchingRef.current = false
      }
    }

    const preventPageZoom = (e: Event) => e.preventDefault()
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
    }
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) e.preventDefault()
    }
    const consumeTouch: AddEventListenerOptions = {
      passive: false,
      capture: true,
    }

    el.addEventListener('pointerdown', onPointerDown, { capture: true })
    el.addEventListener('pointermove', onPointerMove, { capture: true })
    el.addEventListener('pointerup', onPointerUp, { capture: true })
    el.addEventListener('pointercancel', onPointerUp, { capture: true })
    el.addEventListener('touchstart', onTouchStart, consumeTouch)
    el.addEventListener('touchmove', onTouchMove, consumeTouch)
    el.addEventListener('gesturestart', preventPageZoom, consumeTouch)
    el.addEventListener('gesturechange', preventPageZoom, consumeTouch)
    el.addEventListener('gestureend', preventPageZoom, consumeTouch)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown, { capture: true })
      el.removeEventListener('pointermove', onPointerMove, { capture: true })
      el.removeEventListener('pointerup', onPointerUp, { capture: true })
      el.removeEventListener('pointercancel', onPointerUp, { capture: true })
      el.removeEventListener('touchstart', onTouchStart, { capture: true })
      el.removeEventListener('touchmove', onTouchMove, { capture: true })
      el.removeEventListener('gesturestart', preventPageZoom, { capture: true })
      el.removeEventListener('gesturechange', preventPageZoom, { capture: true })
      el.removeEventListener('gestureend', preventPageZoom, { capture: true })
      endPinch()
    }
  }, [containerRef])

  return {
    transform,
    panBy,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToBounds,
    isPinchingRef,
  }
}

type PersistOptions = { flush?: boolean }

const hydrateCanvasDocument = (
  quoteId: number | undefined,
  publicId: string | undefined,
  initial: unknown
): { doc: QuoteCanvasData; imported: boolean } => {
  const suffix = publicId ?? 'draft'
  const parsed = parseCanvasData(initial)
  if (!isCanvasDataEmpty(parsed)) return { doc: parsed, imported: false }
  if (quoteId == null) {
    return { doc: readLocalCanvasDocument(suffix), imported: false }
  }
  const imported = importLegacyLocalCanvas(suffix)
  return imported
    ? { doc: imported, imported: true }
    : { doc: parsed, imported: false }
}

export const useQuoteCanvas = ({
  quoteId,
  publicId,
  initial,
  isReadOnly = false,
  cardIds,
  supabase,
  locale = 'en',
}: {
  quoteId?: number
  publicId?: string
  initial?: unknown
  isReadOnly?: boolean
  cardIds: number[]
  supabase: SupabaseClient<Database>
  locale?: string
}) => {
  const saveCanvasData = useSaveQuoteCanvasData(publicId)
  const storageSuffix = publicId ?? 'draft'
  const hydrated = useRef(hydrateCanvasDocument(quoteId, publicId, initial))
  const [doc, setDoc] = useState<QuoteCanvasData>(hydrated.current.doc)

  const docRef = useRef(doc)
  docRef.current = doc
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastPersistedRef = useRef(
    hydrated.current.imported ? '' : JSON.stringify(doc)
  )
  const hasHydratedRef = useRef(true)
  const scopeRef = useRef(
    quoteId != null ? `q:${quoteId}` : `local:${storageSuffix}`
  )
  const pendingImportRef = useRef(hydrated.current.imported)

  const persistNow = useCallback(
    async (next?: QuoteCanvasData) => {
      if (isReadOnly || !hasHydratedRef.current) return
      const payload =
        quoteId != null
          ? toPersistedCanvasData(next ?? docRef.current)
          : (next ?? docRef.current)
      const serialized = JSON.stringify(payload)
      if (serialized === lastPersistedRef.current) return
      lastPersistedRef.current = serialized
      if (quoteId != null) {
        try {
          await saveCanvasData(quoteId, payload as unknown as Json)
          if (pendingImportRef.current) {
            pendingImportRef.current = false
            clearLocalCanvasKeys(storageSuffix)
          }
        } catch (error) {
          console.error('Failed to save canvas', error)
          lastPersistedRef.current = ''
        }
        return
      }
      writeLocalCanvasDocument(storageSuffix, payload)
    },
    [isReadOnly, quoteId, saveCanvasData, storageSuffix]
  )

  const schedulePersist = useCallback(() => {
    if (persistTimer.current) clearTimeout(persistTimer.current)
    persistTimer.current = setTimeout(() => {
      persistTimer.current = null
      void persistNow()
    }, CANVAS_PERSIST_MS)
  }, [persistNow])

  const updateDoc = useCallback(
    (
      updater: (prev: QuoteCanvasData) => QuoteCanvasData,
      options?: PersistOptions
    ) => {
      setDoc((prev) => {
        const next = updater(prev)
        if (next === prev) return prev
        docRef.current = next
        if (options?.flush) {
          if (persistTimer.current) {
            clearTimeout(persistTimer.current)
            persistTimer.current = null
          }
          void persistNow(next)
        } else {
          schedulePersist()
        }
        return next
      })
    },
    [persistNow, schedulePersist]
  )

  useEffect(() => {
    const scope = quoteId != null ? `q:${quoteId}` : `local:${storageSuffix}`
    if (scopeRef.current === scope) return
    const next = hydrateCanvasDocument(quoteId, publicId, initial)
    scopeRef.current = scope
    hasHydratedRef.current = true
    pendingImportRef.current = next.imported
    hydrated.current = next
    setDoc(next.doc)
    docRef.current = next.doc
    lastPersistedRef.current = next.imported
      ? ''
      : JSON.stringify(
          quoteId != null ? toPersistedCanvasData(next.doc) : next.doc
        )
  }, [initial, publicId, quoteId, storageSuffix])

  useEffect(() => {
    if (pendingImportRef.current) schedulePersist()
  }, [schedulePersist])

  useEffect(() => {
    const flush = () => {
      if (persistTimer.current) {
        clearTimeout(persistTimer.current)
        persistTimer.current = null
      }
      void persistNow()
    }
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [persistNow])

  const idsKey = cardIds.join(',')
  const ids = useMemo(() => cardIds, [idsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ids.length) return
    updateDoc((prev) => {
      const missing = ids.filter((id) => prev.positions[id] == null)
      if (!missing.length) return prev
      const next = { ...prev, positions: { ...prev.positions } }
      const placedCount = ids.length - missing.length
      missing.forEach((id, i) => {
        next.positions[id] = defaultPositionForIndex(placedCount + i)
      })
      return next
    })
  }, [ids, updateDoc])

  const moveCard = useCallback(
    (id: number, position: CardPosition) => {
      updateDoc((prev) => ({
        ...prev,
        positions: { ...prev.positions, [id]: position },
      }))
    },
    [updateDoc]
  )

  const bringToFront = useCallback(
    (id: number) => {
      updateDoc((prev) => ({
        ...prev,
        zOrder: [...prev.zOrder.filter((z) => z !== id), id],
      }))
    },
    [updateDoc]
  )

  const zIndexOf = useCallback(
    (id: number) => {
      const index = doc.zOrder.indexOf(id)
      return index === -1 ? 0 : index + 1
    },
    [doc.zOrder]
  )

  const addComment = useCallback(
    (position: CardPosition, text: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          comments: [
            ...prev.comments,
            {
              id: createId(),
              x: position.x,
              y: position.y,
              text,
              createdAt: new Date().toISOString(),
            },
          ],
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const removeComment = useCallback(
    (id: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          comments: prev.comments.filter((comment) => comment.id !== id),
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const moveComment = useCallback(
    (id: string, position: CardPosition) => {
      updateDoc((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.id === id ? { ...comment, ...position } : comment
        ),
      }))
    },
    [updateDoc]
  )

  const addFiles = useCallback(
    async (incoming: File[], basePosition: CardPosition) => {
      const items = (
        await Promise.all(
          incoming.map(async (file, index) => {
            if (file.size > MAX_IMAGE_PREVIEW_BYTES) {
              console.error('Canvas file too large', file.name, file.size)
              return null
            }
            let url: string | undefined
            if (quoteId != null) {
              url = await uploadCanvasFile(supabase, quoteId, file)
              if (!url) return null
            } else if (file.type.startsWith('image/')) {
              url = await readFileAsDataURL(file).catch((error) => {
                console.error('Failed to read canvas file', error)
                return undefined
              })
            }
            return {
              id: createId(),
              x: basePosition.x + index * 32,
              y: basePosition.y + index * 32,
              name: file.name,
              mimeType: file.type,
              size: file.size,
              url,
              createdAt: new Date().toISOString(),
            }
          })
        )
      ).filter((item): item is NonNullable<typeof item> => item != null)
      if (!items.length) return
      updateDoc(
        (prev) => ({ ...prev, files: [...prev.files, ...items] }),
        { flush: true }
      )
    },
    [quoteId, supabase, updateDoc]
  )

  const moveFile = useCallback(
    (id: string, position: CardPosition) => {
      updateDoc((prev) => ({
        ...prev,
        files: prev.files.map((file) =>
          file.id === id ? { ...file, ...position } : file
        ),
      }))
    },
    [updateDoc]
  )

  const removeFile = useCallback(
    (id: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          files: prev.files.filter((file) => file.id !== id),
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const duplicateFile = useCallback(
    (id: string) => {
      updateDoc((prev) => {
        const file = prev.files.find((f) => f.id === id)
        if (!file) return prev
        return {
          ...prev,
          files: [
            ...prev.files,
            {
              ...file,
              id: createId(),
              x: file.x + 48,
              y: file.y + 48,
              createdAt: new Date().toISOString(),
            },
          ],
        }
      }, { flush: true })
    },
    [updateDoc]
  )

  const bringFileToFront = useCallback(
    (id: string) => {
      updateDoc((prev) => {
        const file = prev.files.find((f) => f.id === id)
        if (!file) return prev
        return {
          ...prev,
          files: [...prev.files.filter((f) => f.id !== id), file],
        }
      })
    },
    [updateDoc]
  )

  const addLink = useCallback(
    async (url: string, position: CardPosition) => {
      const id = createId()
      updateDoc(
        (prev) => ({
          ...prev,
          links: [
            ...prev.links,
            {
              id,
              x: position.x,
              y: position.y,
              url,
              createdAt: new Date().toISOString(),
            },
          ],
        }),
        { flush: true }
      )

      const preview = await fetchLinkPreview(url, { supabase, locale })
      if (!preview.title && !preview.description && !preview.imageUrl) return

      updateDoc(
        (prev) => ({
          ...prev,
          links: prev.links.map((link) =>
            link.id === id
              ? {
                  ...link,
                  title: preview.title,
                  description: preview.description,
                  imageUrl: preview.imageUrl,
                }
              : link
          ),
        }),
        { flush: true }
      )
    },
    [locale, supabase, updateDoc]
  )

  const moveLink = useCallback(
    (id: string, position: CardPosition) => {
      updateDoc((prev) => ({
        ...prev,
        links: prev.links.map((link) =>
          link.id === id ? { ...link, ...position } : link
        ),
      }))
    },
    [updateDoc]
  )

  const removeLink = useCallback(
    (id: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          links: prev.links.filter((link) => link.id !== id),
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const duplicateLink = useCallback(
    (id: string) => {
      updateDoc((prev) => {
        const link = prev.links.find((l) => l.id === id)
        if (!link) return prev
        return {
          ...prev,
          links: [
            ...prev.links,
            {
              ...link,
              id: createId(),
              x: link.x + 48,
              y: link.y + 48,
              createdAt: new Date().toISOString(),
            },
          ],
        }
      }, { flush: true })
    },
    [updateDoc]
  )

  const bringLinkToFront = useCallback(
    (id: string) => {
      updateDoc((prev) => {
        const link = prev.links.find((l) => l.id === id)
        if (!link) return prev
        return {
          ...prev,
          links: [...prev.links.filter((l) => l.id !== id), link],
        }
      })
    },
    [updateDoc]
  )

  const addStroke = useCallback(
    (points: CardPosition[]) => {
      updateDoc(
        (prev) => ({
          ...prev,
          strokes: [
            ...prev.strokes,
            {
              id: createId(),
              points,
              color: STROKE_COLOR,
              width: STROKE_WIDTH,
            },
          ],
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const undoStroke = useCallback(() => {
    updateDoc(
      (prev) => ({ ...prev, strokes: prev.strokes.slice(0, -1) }),
      { flush: true }
    )
  }, [updateDoc])

  const removeStroke = useCallback(
    (id: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          strokes: prev.strokes.filter((stroke) => stroke.id !== id),
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const addStamp = useCallback(
    (position: CardPosition, emoji: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          stamps: [
            ...prev.stamps,
            {
              id: createId(),
              x: position.x,
              y: position.y,
              emoji,
              createdAt: new Date().toISOString(),
            },
          ],
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const moveStamp = useCallback(
    (id: string, position: CardPosition) => {
      updateDoc((prev) => ({
        ...prev,
        stamps: prev.stamps.map((stamp) =>
          stamp.id === id ? { ...stamp, ...position } : stamp
        ),
      }))
    },
    [updateDoc]
  )

  const removeStamp = useCallback(
    (id: string) => {
      updateDoc(
        (prev) => ({
          ...prev,
          stamps: prev.stamps.filter((stamp) => stamp.id !== id),
        }),
        { flush: true }
      )
    },
    [updateDoc]
  )

  const duplicateStamp = useCallback(
    (id: string) => {
      updateDoc((prev) => {
        const stamp = prev.stamps.find((s) => s.id === id)
        if (!stamp) return prev
        return {
          ...prev,
          stamps: [
            ...prev.stamps,
            {
              ...stamp,
              id: createId(),
              x: stamp.x + 48,
              y: stamp.y + 48,
              createdAt: new Date().toISOString(),
            },
          ],
        }
      }, { flush: true })
    },
    [updateDoc]
  )

  return {
    positions: doc.positions,
    moveCard,
    bringToFront,
    zIndexOf,
    comments: doc.comments,
    addComment,
    removeComment,
    moveComment,
    files: doc.files,
    addFiles,
    moveFile,
    removeFile,
    duplicateFile,
    bringFileToFront,
    links: doc.links,
    addLink,
    moveLink,
    removeLink,
    duplicateLink,
    bringLinkToFront,
    strokes: doc.strokes,
    addStroke,
    undoStroke,
    removeStroke,
    stamps: doc.stamps,
    addStamp,
    moveStamp,
    removeStamp,
    duplicateStamp,
  }
}
